/*
 * section-main-article.js — Slice 24 Phase C2 (Article reading UX).
 *
 * Progressive enhancement for sections/main-article.liquid. Loaded via
 * <script defer> ONLY when art_toc != off OR reading progress is on (browser
 * dedupes by URL; a per-section dataset guard keeps editor re-injection safe).
 *
 * Two behaviours:
 *   1. Table of contents — scans [data-article-body] for h2 (+ h3 when
 *      data-toc-depth="h2_h3"), assigns slugified de-duplicated ids, builds
 *      EVERY [data-toc-list] (inline fallback + sidebar copy), reveals each nav,
 *      runs an IntersectionObserver scrollspy per nav (aria-current on the active
 *      link), and smooth-scrolls anchor clicks (instant under
 *      prefers-reduced-motion). No headings => the nav is removed.
 *   2. Reading progress — rAF-throttled scroll handler sets --progress (0..1) on
 *      [data-article-progress] as scaleX; transform-only.
 *
 * Bootstraps on DOMContentLoaded + shopify:section:load. Plain IIFE (J8); no
 * window.* pollution; per-section guard (J12); addEventListener only (J1).
 */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function toArray(list) { return Array.prototype.slice.call(list); }

  function slugify(text, used) {
    var base = (text || '').toLowerCase().trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (!base) { base = 'section'; }
    var id = base;
    var i = 2;
    while (used[id] || document.getElementById(id)) { id = base + '-' + i; i += 1; }
    used[id] = true;
    return id;
  }

  // ---- 1. Table of contents -------------------------------------------
  // A section may carry MORE than one TOC nav (the inline fallback + the
  // sidebar copy). Build every [data-article-toc] off the one shared body;
  // heading-id assignment is idempotent (first nav assigns ids, later navs
  // reuse them), so 0/1/2 navs all work.
  function buildTOC(section) {
    var navs = toArray(section.querySelectorAll('[data-article-toc]'));
    if (!navs.length) { return; }
    var body = section.querySelector('[data-article-body]');
    if (!body) { return; }
    navs.forEach(function (nav) { buildTOCNav(nav, body); });
  }

  function buildTOCNav(nav, body) {
    var list = nav.querySelector('[data-toc-list]');
    if (!list) { return; }

    var depth = nav.getAttribute('data-toc-depth') || 'h2';
    var selector = depth === 'h2_h3' ? 'h2, h3' : 'h2';
    var headings = toArray(body.querySelectorAll(selector));

    if (!headings.length) {
      if (nav.parentNode) { nav.parentNode.removeChild(nav); }
      return;
    }

    var used = {};
    var items = [];
    headings.forEach(function (h) {
      if (h.id) { used[h.id] = true; } else { h.id = slugify(h.textContent, used); }
      var li = document.createElement('li');
      li.className = 'main-article__toc-item';
      if (h.tagName.toLowerCase() === 'h3') { li.className += ' main-article__toc-sub'; }
      var a = document.createElement('a');
      a.className = 'main-article__toc-link';
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      li.appendChild(a);
      list.appendChild(li);
      items.push({ heading: h, link: a });
    });

    nav.hidden = false;

    list.addEventListener('click', function (event) {
      var a = event.target.closest('.main-article__toc-link');
      if (!a) { return; }
      var id = a.getAttribute('href').slice(1);
      var target = document.getElementById(id);
      if (!target) { return; }
      event.preventDefault();
      target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '#' + id);
      }
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });

    initScrollspy(items);
  }

  function initScrollspy(items) {
    if (!('IntersectionObserver' in window)) { return; }
    var current = null;
    var visible = {};

    function setActive(link) {
      if (current === link) { return; }
      if (current) { current.removeAttribute('aria-current'); }
      current = link;
      if (current) { current.setAttribute('aria-current', 'true'); }
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { visible[entry.target.id] = true; }
        else { delete visible[entry.target.id]; }
      });
      var best = null;
      var bestTop = Infinity;
      items.forEach(function (it) {
        if (visible[it.heading.id]) {
          var top = it.heading.getBoundingClientRect().top;
          if (top < bestTop) { bestTop = top; best = it.link; }
        }
      });
      if (best) { setActive(best); }
    }, { rootMargin: '-90px 0px -70% 0px', threshold: [0, 1] });

    items.forEach(function (it) { io.observe(it.heading); });
  }

  // ---- 2. Reading progress --------------------------------------------
  function initProgress(section) {
    var fill = section.querySelector('[data-article-progress]');
    if (!fill) { return; }
    var target = section.querySelector('[data-article-body]') || section;
    var ticking = false;

    function update() {
      ticking = false;
      var rect = target.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var total = rect.height - vh;
      var scrolled = -rect.top;
      var ratio = total > 0 ? scrolled / total : 0;
      if (ratio < 0) { ratio = 0; }
      if (ratio > 1) { ratio = 1; }
      fill.style.setProperty('--progress', String(ratio));
    }

    function onScroll() {
      if (ticking) { return; }
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // ---- bootstrap ------------------------------------------------------
  function initSection(section) {
    if (!section || section.dataset.mainArticleInit === 'true') { return; }
    section.dataset.mainArticleInit = 'true';
    buildTOC(section);
    initProgress(section);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    toArray(scope.querySelectorAll('.section-main-article')).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
